import { FaRegCopy, FaCheck, FaEllipsisV } from "react-icons/fa";
import {
  Badge,
  Button,
  UncontrolledPopover,
  PopoverBody,
} from "reactstrap";
import { useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "../../utils/formatDate";

const CopyCell = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  return (
    <span
      style={{
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
      }}
      onClick={handleCopy}
      title="Copiar"
    >
      <strong>{value}</strong>
      {copied ? <FaCheck color="green" /> : <FaRegCopy />}
    </span>
  );
};

const columnHelper = createColumnHelper();

export const getUserColumns = (
  navigate, 
  onEditClick, 
  onActivateNetwork, 
  onBonusClick,
  onRedAscendente,
  onRangoDistribuidorClick,
  admin
) => {
  const isAdmin = admin && ['ADMIN'].includes(admin?.role);

  const columns = [
    columnHelper.accessor("multyhealth_code", {
      header: "Código",
      cell: ({ getValue }) => <CopyCell value={getValue()} />,
    }),

    columnHelper.accessor("name", {
      header: "Nombre",
      cell: ({ getValue }) => <strong>{getValue()}</strong>,
    }),

    columnHelper.accessor("document", {
      header: "Documento",
      cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
    }),

    columnHelper.accessor("email", {
      header: "Correo",
      cell: ({ getValue }) => <span>{getValue()}</span>,
    }),

    columnHelper.accessor((row) => {
      const ref = row?.reference_id;
      return ref ? `${ref.multyhealth_code} - ${ref.name}` : "-";
    }, {
      id: "sponsor_full",
      header: "Auspiciador",
      cell: ({ getValue }) => getValue(),
    }),

    columnHelper.accessor((row) => row?.network?.influencer_bonus_status, {
      id: "bonus_status",
      header: "Estado Bono",
      cell: ({ getValue }) =>
        <Badge color={getValue() ? "success" : "secondary"}>
          {getValue() ? "Activo" : "Inactivo"}
        </Badge>,
    }),

    columnHelper.accessor((row) => row?.network?.loyalty_status, {
      id: "royalty_status",
      header: "Estado Regalía",
      cell: ({ getValue }) =>
        <Badge color={getValue() ? "success" : "secondary"}>
          {getValue() ? "Activo" : "Inactivo"}
        </Badge>,
    }),

    columnHelper.accessor((row) => row?.network?.activate_date, {
      id: "activate_date",
      header: "Fecha Activación",
      cell: ({ getValue }) =>
        getValue() ? formatDate(getValue()) : "No tiene",
    }),

    columnHelper.display({
      id: "estado",
      header: "Autenticado",
      cell: ({ row }) => {
        const state = row.original.is_confirmed;
        return (
          <Badge color={state ? "success" : "danger"}>
            {state ? "Activo" : "Inactivo"}
          </Badge>
        );
      },
    }),

    columnHelper.accessor("country_id.name", {
      header: "País",
    }),

    columnHelper.accessor("createdAt", {
      header: "Fecha Registro",
      cell: ({ getValue }) => {
        const raw = getValue();
        return raw ? formatDate(raw) : "-";
      },
    }),
  ];

  if (isAdmin) {
    columns.push(
      columnHelper.display({
        id: "activar_red",
        header: "Activar Red",
        size: 500,
        cell: ({ row }) => {
          const user = row.original;
          const canActivate =
            !user?.network?.influencer_bonus_status &&
            !user?.network?.loyalty_status;

          return (
            <Button
              color="success"
              size="sm"
              disabled={!canActivate}
              onClick={() => onActivateNetwork(user)}
            >
              Activar
            </Button>
          );
        },
      })
    );
  }

  columns.push(
    columnHelper.display({
      id: "acciones",
      header: "Acciones",
      cell: ({ row }) => {
        const userId = row.original.id;
        const user = row.original;
        const popoverId = `popover-${userId}`;

        return (
          <div className="d-flex justify-content-center">
            <Button
              id={popoverId}
              color="light"
              size="sm"
              className="border-0 p-1"
              style={{ lineHeight: 1 }}
            >
              <FaEllipsisV />
            </Button>
            <UncontrolledPopover
              trigger="legacy"
              placement="bottom"
              target={popoverId}
            >
              <PopoverBody className="p-1">
                <div
                  className="d-flex flex-column gap-1"
                  style={{ minWidth: 180 }}
                >
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => navigate(`/user/detail/${userId}`)}
                  >
                    <i className="fa fa-eye" /> Ver Detalle
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => navigate(`/user/referral-network/${userId}`)}
                  >
                    <i className="fa fa-sitemap" /> Red
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => onRedAscendente(user)}
                  >
                    <i className="fa fa-gift" /> Red ascendente
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => onEditClick(user)}
                  >
                    <i className="fa fa-edit" /> Editar
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => onBonusClick(user)}
                  >
                    <i className="fa fa-gift" /> Bonificaciones
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    className="text-start d-flex align-items-center gap-2"
                    onClick={() => onRangoDistribuidorClick(user)}
                  >
                    <i className="fa fa-trophy" /> Rango por distribuidor
                  </Button>
                </div>
              </PopoverBody>
            </UncontrolledPopover>
          </div>
        );
      },
    })
  );

  return columns;
};